import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Loader } from '../../components/Loader'
import { ordersApi } from '../../services/api'
import { Order, ORDER_STATUS_LABELS, ORDER_TYPE_LABELS } from '../../types'
import { formatToman, formatDateTimeFa, toFaDigits } from '../../utils/format'
import toast from 'react-hot-toast'

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [order, setOrder] = useState<Order | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    ordersApi
      .getById(id)
      .then((res) => {
        if (res.data.success && res.data.data) setOrder(res.data.data)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [id])

  const cancel = async () => {
    if (!order) return
    try {
      const res = await ordersApi.cancel(order.id)
      if (res.data.success) {
        toast.success('سفارش لغو شد')
        setOrder({ ...order, status: 'CANCELLED' })
      } else {
        toast.error(res.data.message || 'خطا در لغو سفارش')
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'خطا در لغو سفارش')
    }
  }

  if (loading) {
    return (
      <div className="container-main py-20 flex justify-center">
        <Loader />
      </div>
    )
  }

  if (!order) {
    return (
      <div className="container-main py-20 text-center">
        <p className="text-gray-700 mb-4">سفارش یافت نشد.</p>
        <Link to="/orders" className="btn btn-primary">بازگشت به سفارش‌ها</Link>
      </div>
    )
  }

  const statusColor: Record<string, string> = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    CONFIRMED: 'bg-gold-100 text-gold-800',
    PROCESSING: 'bg-blue-100 text-blue-800',
    COMPLETED: 'bg-green-100 text-green-800',
    CANCELLED: 'bg-red-100 text-red-800'
  }

  return (
    <div className="container-main py-10">
      <Link to="/orders" className="text-sm text-gray-500 hover:text-primary-600 mb-4 inline-block">→ بازگشت به سفارش‌ها</Link>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-black text-gray-900" dir="ltr">{order.orderNumber}</h1>
          <p className="text-sm text-gray-500 mt-1">ثبت‌شده در {formatDateTimeFa(order.createdAt)}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`badge px-3 py-1 text-sm ${statusColor[order.status]}`}>{ORDER_STATUS_LABELS[order.status]}</span>
          <span className={`badge px-3 py-1 text-sm ${order.type === 'BUY' ? 'bg-green-100 text-green-800' : 'bg-gold-100 text-gold-800'}`}>
            {ORDER_TYPE_LABELS[order.type]}
          </span>
          {(order.status === 'PENDING' || order.status === 'CONFIRMED') && (
            <button className="btn btn-danger !py-2" onClick={cancel}>لغو سفارش</button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 card p-6">
          <h2 className="font-black text-gray-900 mb-4">اقلام سفارش</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-right text-gray-500 border-b border-gray-200">
                <th className="py-2">محصول</th>
                <th className="py-2">تعداد</th>
                <th className="py-2">وزن (گرم)</th>
                <th className="py-2">قیمت واحد</th>
                <th className="py-2">جمع</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item) => (
                <tr key={item.id} className="border-b border-gray-50">
                  <td className="py-2.5">
                    <span className="font-medium">{item.product?.name || 'محصول'}</span>
                    <div className="text-xs text-gray-400">عیار {toFaDigits(item.product?.purity || 0)}</div>
                  </td>
                  <td className="py-2.5">{toFaDigits(item.quantity)}</td>
                  <td className="py-2.5">{toFaDigits((item.product?.weight || 0) * item.quantity)}</td>
                  <td className="py-2.5">{formatToman(item.unitPrice)}</td>
                  <td className="py-2.5 font-bold">{formatToman(item.totalPrice)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 bg-gray-50 rounded-lg p-4 text-sm flex items-center justify-between">
            <span className="text-gray-600">وزن کل سفارش</span>
            <b className="text-gray-900">
              {toFaDigits(
                order.items.reduce((s, i) => s + (i.product?.weight || 0) * i.quantity, 0)
              )}{' '}
              گرم
            </b>
          </div>

          {order.notes && (
            <div className="bg-gray-50 rounded-lg p-4 mt-4 text-sm">
              <span className="text-gray-500">توضیحات: </span>
              {order.notes}
            </div>
          )}
        </div>

        <div className="card p-6 h-fit">
          <h2 className="font-black text-gray-900 mb-4">جمع مبالغ</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">جمع کالاها</span>
              <span className="font-bold">{formatToman(order.subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">ارسال</span>
              <span className="font-bold">{order.shipping === 0 ? 'رایگان' : formatToman(order.shipping)}</span>
            </div>
            <div className="flex justify-between border-t border-gray-200 pt-3 text-base">
              <span className="font-bold text-gray-900">مبلغ کل</span>
              <span className="font-black text-primary-700">{formatToman(order.total)}</span>
            </div>
          </div>

          {order.shippingAddress && (
            <div className="mt-6 bg-gray-50 rounded-lg p-4 text-sm space-y-1">
              <div className="font-bold text-gray-700 mb-1">اطلاعات ارسال</div>
              <div>{order.shippingAddress.title}</div>
              <div>{order.shippingAddress.province}، {order.shippingAddress.city}</div>
              <div className="text-xs leading-5">{order.shippingAddress.address}</div>
              <div className="text-xs" dir="ltr">{order.shippingAddress.postalCode}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default OrderDetail